import { ReactNode } from 'react'
import clsx from 'clsx'
import { Skeleton, Text } from '@/components'
import FeatureFeedbackBanner from '@/app/components/FeatureFeedback'
import LockedFeatureCard from '@/app/components/LockedFeatureCard'
import FeedbackDialog from '@/authenticated/components/feedback/FeedbackDialog'
import { AlertMessage } from '@/app/components'
import { ChargeNowButton } from '@/authenticated/components/ChargeForecastDashboardView/ChargeNowButton'
import BatteryLimitBar from './BatteryLimitBar'
import ChargeSessionChart from './ChargeSessionChart'
import ChargeSessionDetails from './ChargeSessionDetails'
import StatusAlert from './StatusAlert'
import StatusHeader from './StatusHeader'
import UpcomingRatesCard from './UpcomingRatesCard'
import WhyStatusModal from './WhyStatusModal'
import {
  StatusAlertAction,
  StatusAlertPlacement,
  StatusTone,
} from './ChargeForecastV2.types'
import { ChargeStatus } from './ChargeStatus.utils'
import { ChargeSegment } from './ChargeSessionDetails.utils'
import { ChargeChart } from './ChargeSessionChart.utils'
import { UpcomingRates } from './UpcomingRatesCard.utils'
import {
  UPCOMING_RATES_ID,
  useChargeForecastV2ViewModel,
} from './ChargeForecastV2.viewmodel'

const CARD_BORDERS: Record<StatusTone, string> = {
  'optimizing-paused': 'border-themed-purple-500',
  'optimizing-charging': 'border-themed-green-900',
  'not-optimizing': 'border-themed-grey-200',
}

export type ChargeForecastV2ViewProps = {
  status: ChargeStatus
  segments: ChargeSegment[]
  chargingWindow: string | null
  chart: ChargeChart | null
  upcomingRates: UpcomingRates | null
  // Slots for the pieces that read or write live data, so Storybook can pass
  // static stand-ins (same pattern as PlugSessionsView's feedbackBanner)
  limitBar: ReactNode
  chargeButton: ReactNode | null
  feedbackBanner?: ReactNode
  reportIssueDialog?: ReactNode
  // Confirms a sent report, which the shared dialog doesn't do itself
  reportIssueSent?: boolean
  // A caveat under the chart, e.g. that a charger's plan assumes a full charge
  chartNote?: string
  // Storybook only: show the session details collapsed
  detailsDefaultExpanded?: boolean
  whyModalOpen: boolean
  onAlertAction: (action: StatusAlertAction) => void
  onOpenWhyModal: () => void
  onCloseWhyModal: () => void
  onManageSettings: () => void
}

/**
 * Presentational half, exported for Storybook: renders a computed charge
 * status without fetching anything.
 */
export function ChargeForecastV2View({
  status,
  segments,
  chargingWindow,
  chart,
  upcomingRates,
  limitBar,
  chargeButton,
  feedbackBanner,
  reportIssueDialog,
  reportIssueSent,
  chartNote,
  detailsDefaultExpanded,
  whyModalOpen,
  onAlertAction,
  onOpenWhyModal,
  onCloseWhyModal,
  onManageSettings,
}: ChargeForecastV2ViewProps) {
  // The account has no home, which is the same thing the dashboard's locked
  // card covers, so show that rather than a second no-home design
  if (status.kind === 'NoHome') {
    return (
      <div id="charge-forecast-v2" className="w-full">
        <LockedFeatureCard
          title="Charge Forecast"
          connectionRequirement="home"
        />
      </div>
    )
  }

  // Once a report is sent, the problem stands but the link would only invite
  // a second ticket
  const alert =
    status.alert &&
    reportIssueSent &&
    status.alert.link?.action === 'reportIssue'
      ? { ...status.alert, link: undefined }
      : status.alert
  // Each alert sits with the part of the card it's about: a missing battery
  // level under the battery bar, a missing forecast where the chart goes, a
  // missing rate plan where the rates go. Status-level problems stay on top.
  const alertAt = (placement: StatusAlertPlacement) =>
    alert && (alert.placement ?? 'top') === placement ? (
      <StatusAlert alert={alert} onAction={onAlertAction} />
    ) : null
  const forecastAlert = alertAt('forecast')

  return (
    <div id="charge-forecast-v2" className="flex flex-col w-full gap-8">
      {reportIssueSent && (
        <AlertMessage variant="success" show>
          Thanks, we’ve got your report. Support will follow up by email.
        </AlertMessage>
      )}
      {alertAt('top')}
      <section
        aria-labelledby="charge-forecast-v2-heading"
        className="flex flex-col w-full"
      >
        {/* Same heading as the Charges section below it */}
        <div className="flex items-center gap-2 pb-3">
          <Text variant="h3" id="charge-forecast-v2-heading">
            Current Status
          </Text>
          <span className="bg-themed-purple-200 rounded-full px-2 py-0.5">
            <Text variant="body4" className="!text-themed-purple-500 font-bold">
              New
            </Text>
          </span>
        </div>
        {feedbackBanner && (
          <div className="empty:hidden pb-3">{feedbackBanner}</div>
        )}
        <StatusHeader
          tone={status.tone}
          title={status.title}
          subtitle={status.subtitle}
          lastUpdated={status.lastUpdated}
          infoLabel={
            status.isCharging
              ? 'Why is my car charging?'
              : 'Why is my car paused?'
          }
          onInfoClick={status.showWhyInfo ? onOpenWhyModal : undefined}
        />
        <div
          className={clsx(
            'box-border flex flex-col items-center w-full gap-6 p-6 bg-white border-2 border-t-0 border-solid rounded-b-[20px] drop-shadow-[0_0_5px_rgba(0,0,0,0.1)]',
            CARD_BORDERS[status.tone],
          )}
        >
          {limitBar}
          {alertAt('battery')}
          {(chart || forecastAlert) && (
            <>
              <span className="w-full text-sm font-bold leading-normal text-themed-grey-900">
                Charge Session Forecast
              </span>
              {forecastAlert}
              {chart && <ChargeSessionChart chart={chart} />}
              {chart && chartNote && (
                <span className="w-full -mt-3 text-xs font-medium leading-4 text-themed-grey-500">
                  {chartNote}
                </span>
              )}
            </>
          )}
          {segments.length > 0 && (
            <ChargeSessionDetails
              segments={segments}
              chargingWindow={chargingWindow}
              defaultExpanded={detailsDefaultExpanded}
            />
          )}
          {chargeButton && (
            <>
              <div className="w-full h-px bg-themed-grey-200" />
              <div className="w-full">{chargeButton}</div>
            </>
          )}
        </div>
      </section>
      {upcomingRates && (
        <UpcomingRatesCard id={UPCOMING_RATES_ID} rates={upcomingRates} />
      )}
      {alertAt('rates')}
      <WhyStatusModal
        open={whyModalOpen}
        isCharging={status.isCharging}
        onClose={onCloseWhyModal}
        onManageSettings={onManageSettings}
      />
      {reportIssueDialog}
    </div>
  )
}

/** Exported for Storybook: what the section shows while the forecast loads. */
export function ChargeForecastV2Skeleton() {
  return (
    <div className="flex flex-col w-full gap-3" aria-busy>
      <Skeleton width={120} height={24} />
      <Skeleton width="100%" height={320} />
    </div>
  )
}

type Props = {
  vehicleId: number
}

export default function ChargeForecastV2({ vehicleId }: Props) {
  const viewModel = useChargeForecastV2ViewModel({ vehicleId })

  if (viewModel.isLoading) {
    return <ChargeForecastV2Skeleton />
  }

  return (
    <ChargeForecastV2View
      status={viewModel.status}
      segments={viewModel.segments}
      chargingWindow={viewModel.chargingWindow}
      chart={viewModel.chart}
      upcomingRates={viewModel.upcomingRates}
      limitBar={
        <BatteryLimitBar
          vehicleId={vehicleId}
          batteryPercent={viewModel.batteryPercent}
          onChargeLimitChange={viewModel.setDraggedChargeLimit}
        />
      }
      chargeButton={
        viewModel.showChargeButton ? (
          <ChargeNowButton
            appearance="v2"
            deviceType="vehicle"
            vehicleId={vehicleId}
            className="w-full !p-5"
          />
        ) : null
      }
      feedbackBanner={
        // Renders only once a FeatureFeedback Survey row with this name
        // exists and the customer has a pending response
        <FeatureFeedbackBanner
          surveyName="Charge Forecast Feedback 1"
          question="What do you think about this updated feature?"
        />
      }
      reportIssueDialog={
        // The app's contact form, submitted here so the ticket subject can say
        // which banner sent them and the card can confirm it was sent
        <FeedbackDialog
          id="charge-forecast-v2-report-issue"
          title="Please explain what you were trying to do and what happened when the issue occurred."
          isOpen={viewModel.reportIssueOpen}
          onClose={viewModel.closeReportIssue}
          onSubmit={viewModel.onSubmitReportIssue}
          submitText="Send report"
        />
      }
      reportIssueSent={viewModel.reportIssueSent}
      whyModalOpen={viewModel.whyModalOpen}
      onAlertAction={viewModel.onAlertAction}
      onOpenWhyModal={viewModel.openWhyModal}
      onCloseWhyModal={viewModel.closeWhyModal}
      onManageSettings={viewModel.onManageSettings}
    />
  )
}
